// Client mirror of functions-common/src/repeat/completions.ts. Keep in sync.

import { periodsInWindow } from './window'
import type {
  RecurringWindowInput,
  RepeatItemInput,
  RepeatPageDoc,
  RepeatPagesInput,
} from './types'

export function findPage(
  pages: RepeatPagesInput | null | undefined,
  pageKey: string,
): RepeatPageDoc | undefined {
  if (!pages) return undefined
  if (pages instanceof Map) {
    return pages.get(pageKey)
  }
  if (Array.isArray(pages)) {
    return pages.find((page) => page.pageKey === pageKey)
  }
  return (pages as Record<string, RepeatPageDoc>)[pageKey]
}

export function isRepeatingItem(
  item: Pick<RepeatItemInput, 'checklistType'> | null | undefined,
): boolean {
  return item?.checklistType === 'DAILY' || item?.checklistType === 'WEEKLY'
}

export function effectivePeriodBoardStatus(
  entry: { boardStatus?: string | null } | null | undefined,
  item: Partial<RepeatItemInput> | null | undefined,
  periodKey?: string | null,
): string {
  if (entry?.boardStatus) {
    return entry.boardStatus
  }
  if (
    periodKey &&
    typeof item?.notRequiredFrom === 'string' &&
    item.notRequiredFrom.length > 0 &&
    periodKey >= item.notRequiredFrom
  ) {
    return 'COMPLETE'
  }
  return 'TODO'
}

export function isDoneFor(
  item: RepeatItemInput | null | undefined,
  periodKey: string,
  pages?: RepeatPagesInput | null,
): boolean {
  if (!item) return false

  // Repeating items: only the page for that period counts (plus notRequiredFrom >= periodKey);
  // the definition's own isCompleted / workflowStatus never does (G8)
  if (isRepeatingItem(item)) {
    if (
      typeof item.notRequiredFrom === 'string' &&
      item.notRequiredFrom.length > 0 &&
      periodKey >= item.notRequiredFrom
    ) {
      return true
    }
    if (!pages) return false
    if (Array.isArray(pages)) {
      for (const page of pages) {
        if (page.pageKey === periodKey && page.completions) {
          const completion = page.completions[item.id]
          if (completion) {
            return (
              Boolean(completion.isCompleted) ||
              completion.workflowStatus === 'COMPLETED' ||
              completion.workflowStatus === 'NOT_REQUIRED'
            )
          }
        }
      }
      return false
    }
    const page = findPage(pages, periodKey)
    if (!page?.completions) return false
    const completion = page.completions[item.id]
    if (!completion) return false
    return (
      Boolean(completion.isCompleted) ||
      completion.workflowStatus === 'COMPLETED' ||
      completion.workflowStatus === 'NOT_REQUIRED'
    )
  }

  // One-off (INDIVIDUAL) items: the item's own state, as today
  return (
    Boolean(item.isCompleted) ||
    item.workflowStatus === 'COMPLETED' ||
    item.workflowStatus === 'NOT_REQUIRED'
  )
}

export interface DoneEveryPeriodResult {
  done: boolean
  missed: string[]
}

export function isDoneEveryPeriod(
  item: RepeatItemInput,
  window: RecurringWindowInput | null | undefined,
  pages?: RepeatPagesInput | null,
  until?: Date | string | number | null,
  timeZone?: string,
): DoneEveryPeriodResult {
  const effectiveWindow = window ?? item.recurringWindow
  if (item.repeatPaused === true || effectiveWindow?.repeatPaused === true) {
    return { done: true, missed: [] }
  }
  const cadence = item.checklistType === 'WEEKLY' ? 'WEEKLY' : 'DAILY'
  const allPeriods = periodsInWindow(cadence, effectiveWindow, timeZone, until)
  const notRequiredFrom =
    typeof item.notRequiredFrom === 'string' && item.notRequiredFrom.length > 0
      ? item.notRequiredFrom
      : null
  const periods = notRequiredFrom
    ? allPeriods.filter((period) => period < notRequiredFrom)
    : allPeriods

  const missed: string[] = []
  for (const period of periods) {
    if (!isDoneFor(item, period, pages)) {
      missed.push(period)
    }
  }

  return {
    done: missed.length === 0,
    missed,
  }
}

export const REPEAT_PAGE_STATE_KEYS: ReadonlySet<string> = new Set([
  'workflowStatus',
  'previousWorkflowStatus',
  'boardStatus',
  'isCompleted',
  'completedAt',
  'completedById',
  'completedByName',
  'verifiedCoordinates',
  'statusSyncOrigin',
  'statusSyncVersion',
])

export function isRepeatPageStateKey(key: string): boolean {
  return REPEAT_PAGE_STATE_KEYS.has(key) || key.startsWith('statusSync')
}

